import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    CONSENT_CHANGE_EVENT,
    isAnalyticsEnabled,
    isTrackablePath,
    trackPageView,
} from "@/lib/analytics";

/**
 * AnalyticsRouteListener
 *
 * Sends a manual GA4 `page_view` on every trackable client-side route change.
 * Deduplicated by (pathname + search + document.title) so a re-render or a
 * hash-only change does not double-fire. Admin/internal routes are excluded.
 *
 * IMPORTANT: this component does not itself decide whether to load gtag.js.
 * That decision lives entirely inside `lib/analytics.js` and is gated by
 * user consent. This listener simply asks the module to send a page_view;
 * the module silently drops the call if consent is not granted or the user
 * is opted out or the route is excluded.
 */
export default function AnalyticsRouteListener() {
    const location = useLocation();
    const lastKeyRef = useRef("");
    // A tiny state bump used to re-run the effect when the consent state
    // changes to "granted" — so the very first page view fires immediately
    // after Accept, without waiting for the next navigation.
    const [consentTick, setConsentTick] = useState(0);

    useEffect(() => {
        function onConsentChanged() {
            setConsentTick((n) => n + 1);
        }
        window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChanged);
        return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChanged);
    }, []);

    useEffect(() => {
        if (!isAnalyticsEnabled()) return;
        if (!isTrackablePath(location.pathname)) return;

        // Defer to next tick so any route-driven `document.title` update in
        // useEffect on the destination page has a chance to run first.
        const raf = window.requestAnimationFrame(() => {
            const pageTitle = typeof document !== "undefined" ? document.title : "";
            const pagePath = `${location.pathname}${location.search}`;
            const key = `${pagePath}::${pageTitle}`;
            if (key === lastKeyRef.current) return;
            lastKeyRef.current = key;

            let pageLocation = "";
            try {
                pageLocation = window.location.href;
            } catch (_e) {
                pageLocation = "";
            }

            trackPageView({
                page_location: pageLocation,
                page_path: pagePath,
                page_title: pageTitle,
            });
        });

        return () => window.cancelAnimationFrame(raf);
    }, [location.pathname, location.search, consentTick]);

    return null;
}
